import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { TrackedLink } from "@/components/public/TrackedCta";

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { name: "Operator", path: "/ai-operators" },
    { name: "Build", path: "/app-build" },
    { name: "Rescue", path: "/app-rescue" },
    { name: "Audit", path: "/systems-audit" },
    { name: "Work", path: "/work" },
  ];

  const isActive = (path: string) => location.pathname === path;

  return (
    <nav className="fixed left-0 right-0 top-0 z-50 border-b border-white/10 bg-[#07090c]/95 text-white backdrop-blur-xl">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="flex h-[72px] items-center justify-between">
          <Link to="/" className="group flex items-center gap-3">
            <div className="relative">
              <span className="block text-2xl font-bold tracking-[-0.05em] text-white">
                SVM
              </span>
              <div className="absolute -bottom-1 left-0 right-0 h-[3px] bg-amber-300 transition group-hover:bg-rose-500" />
            </div>
            <span className="hidden text-xs font-bold uppercase tracking-[0.14em] text-slate-400 md:inline">
              Shock Value Media / Public Systems
            </span>
          </Link>

          <div className="hidden items-center gap-4 lg:flex">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                aria-current={isActive(link.path) ? "page" : undefined}
                className={`relative py-6 text-sm font-bold transition-colors hover:text-white after:absolute after:inset-x-0 after:bottom-0 after:h-0.5 after:origin-left after:bg-amber-300 after:transition-transform ${
                  isActive(link.path) ? "text-white after:scale-x-100" : "text-slate-400 after:scale-x-0"
                }`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          <div className="hidden items-center gap-4 lg:flex">
            <TrackedLink
              to="/project-planner"
              tracking={{
                label: "Start a project",
                destination: "/project-planner",
                placement: "desktop_nav",
                service: "project_translator",
              }}
            >
              <Button variant="default" className="rounded-md bg-amber-300 px-5 font-bold text-slate-950 hover:bg-amber-200">
                Start a project
              </Button>
            </TrackedLink>
          </div>

          <button
            type="button"
            aria-label={isOpen ? "Close navigation" : "Open navigation"}
            aria-expanded={isOpen}
            onClick={() => setIsOpen(!isOpen)}
            className="flex h-11 w-11 items-center justify-center rounded-md text-white transition hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300 lg:hidden"
          >
            {isOpen ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>

        {isOpen && (
          <div className="animate-fade-in border-t border-white/10 pb-6 pt-5 lg:hidden">
            <div className="flex flex-col gap-4">
              <Link to="/" onClick={() => setIsOpen(false)} className={`text-base font-bold ${isActive("/") ? "text-white" : "text-slate-400"}`}>Home</Link>
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsOpen(false)}
                  aria-current={isActive(link.path) ? "page" : undefined}
                  className={`text-base font-bold transition-colors hover:text-white ${
                    isActive(link.path) ? "text-white" : "text-slate-400"
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              <TrackedLink
                to="/project-planner"
                onClick={() => setIsOpen(false)}
                tracking={{
                  label: "Start a project",
                  destination: "/project-planner",
                  placement: "mobile_nav",
                  service: "project_translator",
                }}
              >
                <Button variant="default" size="lg" className="w-full rounded-md bg-amber-300 font-bold text-slate-950 hover:bg-amber-200">
                  Start a project
                </Button>
              </TrackedLink>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
